import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';

import { isNullish } from '@plugins/shared/utils/assertFunctions';
import { sizeInputParser } from '@plugins/shared/utils/sizeInputParser/sizeInputParser';

import {
  generateAvatarStyle,
  generateUserNameStyle,
} from '../../../private/utils/utils';
import { TranslatedNameService } from '../../../public/services/translated-name-service';
import { TRANSLATION_KEYS } from '../../config';
import { getNameInitials } from '../../utils/translation-util';
import type {
  AtomicComponentType,
  GeneratedAvatarStyles,
  GeneratedUserNameStyles,
} from '../../utils/types.private';

import { AtomicUserComponent } from './atomic.user.component';

const { ERRORS, ATOMIC } = TRANSLATION_KEYS;

/** Handles template variables in `UserNameComponent` and `UserAvatarComponent`.
 * Input enumeration and data fetching is handled within `AtomicUserComponent`
 * which this class extends. */

// eslint-disable-next-line @angular-eslint/use-component-selector
@Component({
  standalone: false,
  template: '',
  providers: [TranslatedNameService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class AtomicNameAndAvatarComponent extends AtomicUserComponent {
  readonly #GROUP_MULTIPLIER = 0.75;
  readonly errorMessage = ERRORS.requestError;
  componentType!: AtomicComponentType;

  private readonly translatedNameService = inject(TranslatedNameService);

  constructor() {
    super();
    effect(() => {
      this.translatedNameService.updateTranslation(this.nameTranslationKey(), {
        name: this.name(),
      });
    });
  }

  readonly styles = computed(() => {
    if (this.componentType === 'userAvatar') {
      return {
        ...generateAvatarStyle(this.size()),
        ...this.avatarLabelBackground(),
      };
    }

    return generateUserNameStyle(this.size());
  });
  readonly groupIconSize = computed(() => {
    if (!this.size()) return null;
    const parsed = sizeInputParser(this.size());
    if (!parsed) return null;
    return `${parsed.value * this.#GROUP_MULTIPLIER}${parsed.unit}`;
  });
  readonly avatar = computed(() => {
    const entity = this.userSignal();
    if (!entity || entity.type === 'error') return undefined;
    return entity.avatar;
  });

  readonly initials = computed(() => getNameInitials(this.userSignal()));
  readonly name = computed(() => {
    const entity = this.userSignal();
    if (!entity || entity.type === 'error') return '';
    return entity.name;
  });
  readonly nameTranslationKey = computed(() => {
    const entity = this.userSignal();
    if (!entity) return '';
    switch (entity.type) {
      case 'error':
        return ERRORS.requestError;
      case 'missing':
        return ATOMIC.missing;
      default:
        return ['userDetails', entity.type].join('.');
    }
  });
  /** Exposed translated entity name */
  translatedName$ = this.translatedNameService.translatedName$;
  readonly titleTranslationKey = computed<string>(() => {
    if (!this.showTitle()) return '';

    const [hasDetailed, currentStatus, entity] = [
      this.detailedTitle(),
      this.status(),
      this.userSignal(),
    ];
    // Component's logic ensures that the userSignal is nullish only during pending status.
    if (currentStatus === 'pending' || isNullish(entity)) {
      return ATOMIC.loading;
    }

    if (currentStatus === 'error' || entity.type === 'error') {
      return ERRORS.requestError;
    }
    if (entity.type === 'missing') {
      return ATOMIC.missing;
    }
    if (entity.type === 'user') {
      return ATOMIC[hasDetailed ? 'userDetail' : 'user'];
    }
    return hasDetailed
      ? entity.userCount === 1
        ? ATOMIC.groupDetailSingle
        : ATOMIC.groupDetailNonSingle
      : ATOMIC.group;
  });

  readonly count = computed(() => {
    const user = this.userSignal();
    if (!user || user.type !== 'group') return undefined;
    return user.userCount;
  });

  abstract avatarLabelBackground(): { backgroundColor: string };

  protected setStyles(): GeneratedAvatarStyles | GeneratedUserNameStyles {
    if (this.componentType === 'userAvatar') {
      return {
        ...generateAvatarStyle(this.size()),
        ...this.avatarLabelBackground(),
      };
    }

    return generateUserNameStyle(this.size());
  }
}
